import { fetchUtil } from "./fetchUtil";
import { store } from "../store";
import { MediaServerTypes, MediaType } from "../constants/";
import { MediaUpload } from "../api/api.service";
import { removeSpaceUnderscoreCharacterFromString } from "./commonUtils";

let isConfigUpdated = false;
const KB_CONVERTER = 1024;
export const allowedMediaExtensions = {
    [MediaType.IMAGE]: ["png", "jpg", "jpeg"],
    [MediaType.AUDIO]: ["m4a", "mp3", "wav"]
};

export const allowedMediaSize = {
    [MediaType.IMAGE]: 25 * 1024, // 5MB in KB
    [MediaType.AUDIO]: 300 * 1024 // 100MB in KB
};

export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const toBase64 = (file, callback) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
        callback(reader.result);
    };
    reader.onerror = function (error) {};
};

export const initializeMediaUtil = (file, isPublic = true) => {
    const token = store.getState().userAuth.user.Token;

    // Determine the file type
    const fileType = file.type.startsWith("audio/")
        ? "AUDIO"
        : file.type.startsWith("image/")
        ? "IMAGE"
        : "DOCUMENT";

    return fetchUtil({
        url: isPublic ? "/media/public/init" : "/media/init",
        body: JSON.stringify({
            name: file.name,
            type: fileType, // AUDIO, IMAGE, or DOCUMENT
            size: file.size,
            public: true // Use isPublic parameter to set this value
        }),
        audio: fileType === "AUDIO",
        image: fileType === "IMAGE",
        method: "POST",

        ...(!isPublic && { token }) // Add token for private uploads
    })
        .then((res) => {
            console.log("Response:", res);
            return Promise.resolve(res);
        })
        .catch((error) => {
            console.log("Error:", error);
            return Promise.reject(error);
        });
};

export const uploadOnS3 = async (stream, credentials, cb) => {
    // Ensure AWS SDK is loaded
    if (!window.AWS) {
        console.error("AWS SDK is not available.");
        return;
    }

    // Update AWS configuration
    if (!isConfigUpdated) {
        window.AWS.config.update({ region: credentials.region });
        isConfigUpdated = true;
    }

    // Create an S3 instance
    const s3 = new window.AWS.S3({
        credentials: new window.AWS.Credentials({
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken
        }),
        signatureVersion: "v4",
        region: credentials.region
    });

    try {
        // Determine if the stream is a File or Blob
        let blob;
        if (stream?.stream instanceof Blob) {
            // Direct File object (e.g., audio files)
            blob = stream.stream;
        } else if (stream instanceof File || stream instanceof Blob) {
            // Cropped image stream
            blob = stream;
        } else {
            const contentType = stream.type; // e.g., "audio/mpeg" for MP3 or "image/jpeg" for JPEG images
            blob = new Blob([stream], { type: contentType });
            // throw new Error("Unsupported stream object. Ensure it is a File or Blob.");
        }

        // Upload the Blob or File to S3
        const uploadedFile = await s3
            .upload({
                Bucket: credentials.bucket,
                Key: credentials.mediaPath, // S3 path for storage
                Body: blob, // Upload body
                ContentType: blob.type || "application/octet-stream", // Default MIME type
                ACL: "public-read" // Optional: File accessibility
            })
            .on("httpUploadProgress", (progress) => {
                if (cb) {
                    cb(progress.loaded / progress.total); // Progress callback
                }
            })
            .promise();

        return uploadedFile; // Return uploaded file data
    } catch (error) {
        console.error("Error during file upload:", error);
        throw error;
    }
};

export const getUploadedProgress = (uploadedSize, totalSize) => {
    let uploadedProgress = (uploadedSize / totalSize) * 100;
    return Number(uploadedProgress.toFixed(0));
};

export const getMediaStream = async (file) => {
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = (e) => {
            resolve(e.target.result);
        };
        reader.onerror = (err) => {
            reject(false);
        };
        reader.readAsArrayBuffer(file);
    });
};

export const finalizeMediaUtil = (id, isPublic = true) => {
    const token = store.getState().userAuth.user.Token;

    return fetchUtil({
        url: isPublic ? "/media/public/finalize" : "/media/finalize",
        body: JSON.stringify({
            id
        }),
        method: "POST",
        ...(!isPublic && { token })
    })
        .then((res) => {
            return Promise.resolve(res);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

export const getMediaPath = (media = null) => {
    if (media && media.BasePath && media.Path) {
        return `${media.BasePath}${media.Path}`;
    }
    return "";
};

export const getImageFromMedia = (media) => {
    for (let i in media) {
        if (media[i].Type === MediaServerTypes.Image) {
            return media[i];
        }
    }
    return "";
};

export const getPreviewFile = (file) => {
    if (file && file.name) {
        return file && URL.createObjectURL(file);
    }
    return file;
};

export const isImageInvalid = (evt, sourceImage) => {
    evt.target.src = sourceImage;
};

export const imageUploadHandler = ({
    file,
    imagePreviewCB = () => {},
    imageErrorCB = () => {},
    uploadImgCB = () => {},
    sizeValidation = true,
    typeValidation = true,
    fileTypes = [],
    customErrorMessage = ""
}) => {
    const errorMessage = customErrorMessage
        ? customErrorMessage
        : "Please upload the image in either JPEG or PNG format.";
    if (typeValidation) {
        if (!validateImageType(file, fileTypes)) {
            imageErrorCB(errorMessage);
            imagePreviewCB(undefined);
            uploadImgCB("");
            return "typeError";
        }
    }
    if (sizeValidation) {
        if (!validateImageSize(file, 50, 0)) {
            imageErrorCB("Please make sure the image size should be less than 5MB.");
            imagePreviewCB(undefined);
            uploadImgCB("");
            return "sizeError";
        }
    }
    imagePreviewCB(URL.createObjectURL(file));
    let modifiedImageName = removeSpaceUnderscoreCharacterFromString(file?.name);
    let image = file;
    image.fileName = modifiedImageName;
    imageErrorCB(undefined);
    uploadImgCB(image);
};

export const validateImageType = (file, imageTypes) => {
    const uploadedFileType = file?.name
        ?.split(".")
        [file?.name?.split(".").length - 1]?.toLowerCase();
    return imageTypes.includes(uploadedFileType);
};

export const validateImageSize = (file, maxSize, minSize) => {
    let fileSize = file && file?.size;
    fileSize = fileSize / (KB_CONVERTER * KB_CONVERTER);
    return fileSize >= minSize && fileSize <= maxSize;
};

export const uploadMediaToServer = async (file, progressCB) => {
    try {
        progressCB(true);
        const formData = new FormData();
        formData.append("file", file);
        const { Id } = await MediaUpload(formData);
        return Number.parseInt(Id);
    } catch (err) {
        return;
    } finally {
        progressCB(false);
    }
};

export const createImage = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => resolve(img);
        img.onerror = reject;
    });

export const fileUploadHandler = ({
    file,
    mediaType, // MediaType.IMAGE or MediaType.AUDIO
    previewCB = () => {},
    errorCB = () => {},
    uploadFileCB = () => {},
    customErrorMessage = ""
}) => {
    const extensions = allowedMediaExtensions[mediaType];
    const maxSizeKB = allowedMediaSize[mediaType];

    const errorMessage = customErrorMessage
        ? customErrorMessage
        : `Please upload a valid ${mediaType.toLowerCase()} file. Supported formats: ${extensions.join(
              ", "
          )}, Max size: ${(maxSizeKB / 1024).toFixed(2)}MB.`;

    // Validate file name (it should be at least 1 character)
    if (!file.name || file.name.trim().length < 1) {
        errorCB("The file name is required and should have at least 1 character.");
        previewCB(undefined);
        uploadFileCB("");
        return "nameError";
    }

    // Validate file type (check if the file has a valid extension)
    if (!validateFileType(file, extensions)) {
        errorCB(`Invalid file type. ${errorMessage}`);
        previewCB(undefined); // Reset the preview
        uploadFileCB(""); // Reset the file value
        return "typeError";
    }

    if (file.type.startsWith("image/") || file.type.startsWith("audio/")) {
        previewCB(URL.createObjectURL(file)); // Set file preview URL
    }

    // Validate file size (check if the file exceeds the max size in KB)
    if (!validateFileSize(file, maxSizeKB)) {
        const message = `File size exceeds the limit of ${(maxSizeKB / 1024).toFixed(2)}MB.`;
        errorCB(message);
        previewCB(undefined); // Reset the preview
        uploadFileCB(""); // Reset the file value
        return "sizeError";
    }

    // Validate MIME type (ensure it's a supported type)
    const allowedMimeTypes = {
        [MediaType.IMAGE]: ["image/jpeg", "image/png", "image/gif", "image/bmp"],
        [MediaType.AUDIO]: ["audio/mpeg", "audio/wav", "audio/mp3"]
    };

    if (!allowedMimeTypes[mediaType].includes(file.type)) {
        const message = `Unsupported file type. Supported types: ${allowedMimeTypes[mediaType].join(
            ", "
        )}`;
        errorCB(message);
        previewCB(undefined); // Reset the preview
        uploadFileCB(""); // Reset the file value
        return "typeError";
    }

    // Generate preview URL if the file is an image or audio
    if (file.type.startsWith("image/") || file.type.startsWith("audio/")) {
        previewCB(URL.createObjectURL(file)); // Set file preview URL
    } else {
        previewCB(undefined); // No preview available
    }

    errorCB(undefined); // Clear any previous errors
    uploadFileCB(file); // Pass the validated file to the upload callback
};

// Validate file type
export const validateFileType = (file, supportedTypes) => {
    const fileExtension = file?.name?.split(".")?.pop()?.toLowerCase();
    return supportedTypes.includes(fileExtension);
};

// Validate file size
export const validateFileSize = (file, maxSize) => {
    const fileSizeInKB = file?.size / 1024; // Convert size to KB
    return fileSizeInKB <= maxSize;
};

export const formatFileSize = (size) => {
    const KB_CONVERTER = 1024; // Define the conversion factor
    return parseInt(size / KB_CONVERTER); // Return size in KB, rounded to 2 decimal places
};
